import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import PositionList from "@/components/board/PositionList";
import ActivityList from "@/components/board/ActivityList";
import Scoreboard from "@/components/board/Scoreboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertTriangle, Coffee, Phone, Play, Pause, ClipboardList, Plus } from "lucide-react";

const FREEZE_REASONS = ["Lunch", "On Phone", "Meeting", "Restroom", "Personal"];
const OUTCOMES = [
  { v: "sold", l: "Sold" },
  { v: "lost", l: "Lost" },
  { v: "follow_up", l: "Follow-Up Needed" },
  { v: "delivery_scheduled", l: "Delivery Scheduled" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(user?.role);

  const [queue, setQueue] = useState({ floor: [], phone: [] });
  const [opps, setOpps] = useState([]);
  const [score, setScore] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [outcome, setOutcome] = useState("sold");
  const [commentText, setCommentText] = useState("");

  const refreshAll = useCallback(async () => {
    try {
      const [q, o, s, d, u, a] = await Promise.all([
        api.get("/queue"),
        api.get("/opportunities"),
        api.get("/scoreboard"),
        api.get("/dealership"),
        api.get("/users"),
        api.get("/alerts"),
      ]);
      setQueue(q.data);
      setOpps(o.data);
      setScore(s.data);
      setDealer(d.data);
      setUsers(u.data);
      setAlerts(a.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useWebSocket((msg) => {
    if (["queue_updated", "opportunity_created", "opportunity_updated", "to_requested", "to_accepted", "alert_acked", "dealership_updated", "roster_updated"].includes(msg.event)) {
      refreshAll();
    }
    if (msg.event === "to_requested" && isManager) {
      toast.warning(`T.O. requested by ${msg.payload?.salesperson_name}`, { duration: 6000 });
    }
    if (msg.event === "unattended_guest") {
      toast.error(`UNATTENDED GUEST — ${msg.payload?.location}`, { duration: 8000 });
      refreshAll();
    }
  });

  // Actions
  const myFloor = queue.floor.find((q) => q.user_id === user?.user_id);
  const myStatus = myFloor?.status || "off_shift";

  const handleClockIn = async () => { await api.post("/queue/clock-in"); toast.success("Clocked in"); };
  const handleClockOut = async () => { await api.post("/queue/clock-out"); toast.success("Clocked out"); };
  const handleFreeze = async (reason) => { await api.post("/queue/freeze", { reason }); toast.success(`Status: ${reason}`); };
  const handleUnfreeze = async () => { await api.post("/queue/unfreeze"); toast.success("Available"); };
  const fireUnattended = async () => {
    await api.post("/alerts/unattended-guest", { location: "Showroom" });
    toast.error("Unattended guest alert fired");
  };
  const handleRotate = async () => { await api.post("/queue/rotate"); toast.success("Rotated #1 to bottom"); };
  const ackAlert = async (id) => { await api.post(`/alerts/${id}/ack`); };

  const handleAdvance = async (o) => {
    try { await api.post(`/opportunities/${o.opp_id}/advance`); toast.success("Step advanced"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not advance"); }
  };
  const handleRequestTO = async (o) => { await api.post(`/opportunities/${o.opp_id}/request-to`); toast.success("T.O. requested"); };
  const handleAcceptTO = async (o) => { await api.post(`/opportunities/${o.opp_id}/accept-to`); toast.success("T.O. accepted"); };
  const handleCloseOpp = async () => {
    if (!closeTarget) return;
    await api.post(`/opportunities/${closeTarget.opp_id}/close`, { outcome });
    toast.success("Opportunity closed & sent to CRM");
    setCloseTarget(null);
  };

  const addComment = async () => {
    if (!commentText.trim() || !selectedOpp) return;
    const r = await api.post(`/opportunities/${selectedOpp.opp_id}/comment`, { text: commentText });
    setSelectedOpp({ ...selectedOpp, comments: [...(selectedOpp.comments || []), r.data] });
    setCommentText("");
  };

  return (
    <Layout>
      {/* Alert bar */}
      {alerts.length > 0 && (
        <div className="border-b border-[#FF3B30]/40 bg-[#FF3B30]/10">
          <div className="px-4 md:px-6 py-2 flex items-center gap-3 overflow-x-auto">
            {alerts.map((a) => (
              <button
                key={a.alert_id}
                onClick={() => ackAlert(a.alert_id)}
                className="flex items-center gap-2 px-3 h-8 bg-[#FF3B30] text-white text-xs uppercase tracking-wider font-bold rounded-sm alert-pulse shrink-0"
                data-testid={`alert-${a.alert_id}`}
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {a.type === "to_request" ? `T.O. — ${a.salesperson_name}` : `UNATTENDED ${a.location || ""}`}
                <span className="opacity-60">tap to ack</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status / actions strip */}
      <div className="border-b border-white/10 px-4 md:px-6 py-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 mr-1">My Status:</span>
        <span className="text-xs uppercase tracking-wider font-bold px-2 py-1 bg-[#1F1F1F]" data-testid="my-status-badge">
          {myStatus.replace("_", " ")}
        </span>
        {myStatus === "off_shift" ? (
          <Button size="sm" onClick={handleClockIn} className="bg-[#34C759] text-black hover:bg-[#2bb850] uppercase tracking-wider text-[10px] h-8 rounded-sm font-bold" data-testid="clock-in-btn">
            <Play className="w-3 h-3 mr-1" /> Clock In
          </Button>
        ) : (
          <>
            {(myStatus === "available" || myStatus === "on_point") && (
              <>
                {FREEZE_REASONS.map(r => (
                  <Button key={r} size="sm" variant="outline" onClick={() => handleFreeze(r)} className="border-white/10 text-white/70 hover:text-white hover:bg-white/5 uppercase tracking-wider text-[10px] h-8 rounded-sm" data-testid={`freeze-${r.toLowerCase()}-btn`}>
                    {r === "Lunch" ? <Coffee className="w-3 h-3 mr-1" /> : r === "On Phone" ? <Phone className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                    {r}
                  </Button>
                ))}
              </>
            )}
            {(myStatus === "frozen" || myStatus === "on_break") && (
              <Button size="sm" onClick={handleUnfreeze} className="bg-[#34C759] text-black hover:bg-[#2bb850] uppercase tracking-wider text-[10px] h-8 rounded-sm font-bold" data-testid="unfreeze-btn">
                <Play className="w-3 h-3 mr-1" /> I'm Available
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={handleClockOut} className="text-white/50 hover:text-white text-[10px] uppercase tracking-wider h-8 rounded-sm" data-testid="clock-out-btn">
              Clock Out
            </Button>
          </>
        )}

        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-[#007AFF] text-white hover:bg-[#005bb5] uppercase tracking-wider text-[10px] h-8 rounded-sm font-bold" data-testid="claim-up-btn">
            <Plus className="w-3 h-3 mr-1" /> Claim / New Up
          </Button>
          {isManager && (
            <>
              <Button size="sm" variant="outline" onClick={fireUnattended} className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 uppercase tracking-wider text-[10px] h-8 rounded-sm font-bold" data-testid="unattended-btn">
                <AlertTriangle className="w-3 h-3 mr-1" /> Unattended Guest
              </Button>
              <Button size="sm" variant="outline" onClick={handleRotate} className="border-white/10 text-white/70 hover:text-white hover:bg-white/5 uppercase tracking-wider text-[10px] h-8 rounded-sm" data-testid="rotate-btn">
                Rotate #1
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main grid */}
      <div className="p-4 md:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        <div className="lg:col-span-3">
          <Tabs defaultValue="floor">
            <TabsList className="grid grid-cols-2 w-full bg-[#141414] border border-white/10 rounded-sm h-10 p-0">
              <TabsTrigger value="floor" className="data-[state=active]:bg-white data-[state=active]:text-black uppercase tracking-wider text-xs font-bold rounded-sm" data-testid="tab-floor">Floor Ups</TabsTrigger>
              <TabsTrigger value="phone" className="data-[state=active]:bg-white data-[state=active]:text-black uppercase tracking-wider text-xs font-bold rounded-sm" data-testid="tab-phone">Phone Ups</TabsTrigger>
            </TabsList>
            <TabsContent value="floor" className="mt-4">
              <PositionList data={queue.floor} queueType="floor" rotationMinutes={dealer?.power_rotation_minutes} title="Floor Queue" />
            </TabsContent>
            <TabsContent value="phone" className="mt-4">
              <PositionList data={queue.phone} queueType="phone" rotationMinutes={dealer?.power_rotation_minutes} title="Phone Queue" />
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-6">
          <div className="flex items-baseline justify-between border-b border-white/10 pb-2 mb-3">
            <h2 className="font-display text-2xl font-black uppercase tracking-wider">Activity Board</h2>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">{opps.length} active</span>
          </div>
          <ActivityList
            opportunities={opps}
            processSteps={dealer?.process_steps}
            currentUser={user}
            onAdvance={handleAdvance}
            onRequestTO={handleRequestTO}
            onAcceptTO={handleAcceptTO}
            onClose={(o) => { setCloseTarget(o); setOutcome("sold"); }}
            onSelect={(o) => setSelectedOpp(o)}
            onComment={() => {}}
          />
        </div>

        <div className="lg:col-span-3">
          <Scoreboard data={score} />
        </div>
      </div>

      {/* Create Opportunity Dialog */}
      <CreateOppDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        users={users.filter(u => u.active && ["salesperson", "bdc", "floor_manager"].includes(u.role))}
        currentUser={user}
        onCreated={() => { setCreateOpen(false); refreshAll(); toast.success("Opportunity started"); }}
      />

      {/* Detail Dialog */}
      <Dialog open={!!selectedOpp} onOpenChange={(v) => !v && setSelectedOpp(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-xl rounded-sm">
          {selectedOpp && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-wider text-2xl">
                  {selectedOpp.customer_name || "Unnamed Guest"}
                </DialogTitle>
                <DialogDescription className="text-white/50">
                  {selectedOpp.salesperson_name} · {selectedOpp.vehicle_of_interest || "No VOI"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Comments</div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {(selectedOpp.comments || []).map((c, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-white/5 p-2.5 text-sm">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{c.by_name} · {new Date(c.at).toLocaleTimeString()}</div>
                      <div>{c.text}</div>
                    </div>
                  ))}
                  {!selectedOpp.comments?.length && <div className="text-xs text-white/30 italic">No comments yet.</div>}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a note (e.g., 'Customer wants to think it over, follow up tomorrow')"
                    className="bg-[#0A0A0A] border-white/10 rounded-sm min-h-[60px]"
                    data-testid="comment-input"
                  />
                </div>
                <Button onClick={addComment} className="bg-white text-black hover:bg-white/90 rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="add-comment-btn">
                  Add Note
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Close Out Dialog */}
      <Dialog open={!!closeTarget} onOpenChange={(v) => !v && setCloseTarget(null)}>
        <DialogContent className="bg-[#141414] border-white/10 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider text-xl">Close Opportunity</DialogTitle>
            <DialogDescription className="text-white/50">
              Outcome will be sent to your CRM lead-routing email.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Outcome</Label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" data-testid="outcome-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {OUTCOMES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button onClick={handleCloseOpp} className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold" data-testid="confirm-close-btn">
              Close & Send to CRM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function CreateOppDialog({ open, onOpenChange, users, currentUser, onCreated }) {
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(currentUser?.role);
  const [type, setType] = useState("walk_in");
  const [customer, setCustomer] = useState("");
  const [voi, setVoi] = useState("");
  const [spId, setSpId] = useState("");

  useEffect(() => {
    if (open) { setType("walk_in"); setCustomer(""); setVoi(""); setSpId(currentUser?.user_id || ""); }
  }, [open, currentUser]);

  const submit = async () => {
    try {
      await api.post("/opportunities", {
        type,
        customer_name: customer,
        vehicle_of_interest: voi,
        salesperson_id: isManager && spId ? spId : undefined,
      });
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-white/10 rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider text-xl">New Opportunity</DialogTitle>
          <DialogDescription className="text-white/50">Claim an up & log it on the activity board.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" data-testid="opp-type-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                <SelectItem value="walk_in">Walk-In</SelectItem>
                <SelectItem value="appointment">Appointment (Shown)</SelectItem>
                <SelectItem value="be_back">Be-Back</SelectItem>
                <SelectItem value="phone_up">Phone Up</SelectItem>
                <SelectItem value="off_site">Off-Site Appointment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isManager && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">Assign To</Label>
              <Select value={spId} onValueChange={setSpId}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" data-testid="opp-assign-select"><SelectValue placeholder="Salesperson" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10">
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.display_name || u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Customer Name</Label>
            <Input value={customer} onChange={(e) => setCustomer(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" placeholder="John Smith" data-testid="opp-customer-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60">Vehicle of Interest</Label>
            <Input value={voi} onChange={(e) => setVoi(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" placeholder="2024 Civic Sport" data-testid="opp-voi-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold" data-testid="create-opp-submit">
            <ClipboardList className="w-3 h-3 mr-1" /> Start Opportunity
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
